<script setup lang="ts">
/**
 * UI-kit component
 * Category: 'Info elements/other'
 * Name: 'Notifications'
 * FIGMA: 'https://www.figma.com/design/CFyOwQUjj7uVRQTc03muvG/Demo-CS?node-id=7680-5412&m=dev'
 */
import type { Toast } from '@UI/Toast/ToastProvider/types/interfaces'

import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { useToastItem } from '@UI/Toast/ToastProvider/composables/use-toast-item.hook'
import { BtnIcon } from '@UI/Buttons/BtnIcon'
import { IconContainer } from '@UI/Other/IconContainer'
import { AppText } from '@UI/Typography/AppText'

defineOptions({
  name: 'ToastItem',
})

const props = defineProps<ToastItemProps>()

const emit = defineEmits<{
  dismiss: [id: string]
}>()

interface ToastItemProps {
  toast: Toast
}

const {
  icons,
  progress,
  swipeStyle,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  pauseProgress,
  resumeProgress,
} = useToastItem({ toast: props.toast })

const handlePointerUp = (): void => {
  onPointerUp({
    onEmitDismiss: () => {
      emit('dismiss', props.toast.id)
    },
  })
}
</script>

<template>
  <div
    :class="['toast-item', `toast-item--${toast.variant}`]"
    :style="swipeStyle"
    role="alert"
    aria-live="assertive"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerUp"
    @mouseenter="pauseProgress"
    @mouseleave="resumeProgress"
  >
    <div class="toast-item__content">
      <IconContainer
        :class="`toast-icon toast-icon--${toast.variant}`"
      >
        <FontAwesomeIcon
          :icon="icons[toast.variant]"
        />
      </IconContainer>
      <div class="toast-item__text">
        <AppText
          v-if="toast.title"
          name="body1-medium"
        >
          {{ toast.title }}
        </AppText>
        <AppText
          name="body1"
          tag="span"
          color="slate-700"
        >
          {{ toast.description }}
        </AppText>
      </div>
      <BtnIcon
        v-if="toast.closable"
        :icon="faXmark"
        view="transparent"
        aria-label="Close"
        size="small"
        @click:close="emit('dismiss', toast.id)"
        @pointerdown.stop
        @pointerup.stop
      />
    </div>
    <div
      v-if="toast.progress && toast.duration && toast.duration > 0"
      class="toast-item__progress"
    >
      <div
        class="toast-item__progress-bar"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_toast-item.scss" />
